// © Microsoft Corporation. All rights reserved.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Label, Spinner, SpinnerSize } from '@fluentui/react';
import { RemoteVideoStream, VideoStreamRenderer, VideoStreamRendererView } from '@azure/communication-calling';
import { videoHint, mediaContainerWithLabel, mediaContainerNoLabel, LabelContainer, LabelContainerNoRelative, hiddenStyle } from './styles/StreamMedia.styles';
import { ShouldShowLabel, utils, ShouldUserBeVisible } from 'Utils/Utils';
import staticMediaSVG from '../assets/staticmedia.svg';
import { Image, ImageFit } from '@fluentui/react';

export interface RemoteStreamMediaProps {
    label: string;
    stream: RemoteVideoStream | undefined;
    isParticipantStreamSelected: boolean;
}

export default (props: RemoteStreamMediaProps): JSX.Element => {
    const rendererViewRef = useRef<VideoStreamRendererView>();
    const streamId = props.stream ? utils.getStreamId(utils.getPinFromLabel(props.label), props.stream) : `${utils.getPinFromLabel(props.label)} - no stream`;
    const [activeStreamBeingRendered, setActiveStreamBeingRendered] = useState(false);
    const [showRenderLoading, setShowRenderLoading] = useState(false);

    const imageProps = {
        src: staticMediaSVG.toString(),
        imageFit: ImageFit.contain,
        styles: {
            root: {
                width: '100%',
                height: '80%',
                display: activeStreamBeingRendered ? 'none' : 'block',
                position: 'inherit' as 'inherit'
            }
        }
    };

    const loadingStyle = {
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    };

    const { label, stream, isParticipantStreamSelected } = props;

    const renderRemoteStream = useCallback(async () => {
        try {
            const container = document.getElementById(streamId);
            if (container && stream && stream.isAvailable && isParticipantStreamSelected) {
                // if we are already rendering a stream we don't want to start rendering the same stream
                if (activeStreamBeingRendered) {
                    return;
                }

                // set the flag that a stream is being rendered
                setActiveStreamBeingRendered(true);
                setShowRenderLoading(true);
                const renderer: VideoStreamRenderer = new VideoStreamRenderer(stream);
                // this can block a really long time if we fail to be subscribed to the call and it has to retry
                rendererViewRef.current = await renderer.createView({ scalingMode: 'Fit' });

                var videoList = rendererViewRef.current.target.getElementsByTagName("video");
                for (let i = 0; i < videoList.length; i++) {
                    videoList[i].setAttribute("style", "width: 100%; height: 100%; position: absolute;");
                    videoList[i].attributes.setNamedItem(document.createAttribute("disablePictureInPicture"));
                }

                setShowRenderLoading(false);
                if (container && container.childElementCount === 0) {
                    container.appendChild(rendererViewRef.current.target);
                }
            }
            else {
                setActiveStreamBeingRendered(false);

                if (rendererViewRef.current) {
                    rendererViewRef.current.dispose();
                    rendererViewRef.current = undefined;
                }
            }
        }
        catch (err) {
            console.error(err);

            const multipleClicksErrCode = 4;

            if ((err as any)?.code === multipleClicksErrCode)
            {
                rendererViewRef.current?.dispose();
                rendererViewRef.current = undefined;
            }
        }
        
    }, [
        stream,
        streamId,
        isParticipantStreamSelected,
        activeStreamBeingRendered,
        setShowRenderLoading,
        setActiveStreamBeingRendered
    ]);
    useEffect(() => {
        return (): void => {
            if (rendererViewRef) {
                rendererViewRef.current?.dispose();
                rendererViewRef.current = undefined;
                setActiveStreamBeingRendered(false);
            }
        };
    }, [stream]);

    useEffect(() => {
        if (!stream) {
            return;
        }

        stream.on('isAvailableChanged', renderRemoteStream);
        if (stream.isAvailable) {
            renderRemoteStream();
        }
    }, [stream, isParticipantStreamSelected, renderRemoteStream]);

    return (

        <div className={ShouldUserBeVisible(props.label) ? mediaContainerNoLabel : hiddenStyle}>
            <div
                style={{ display: activeStreamBeingRendered && ShouldUserBeVisible(props.label) ? 'block' : 'none' }}
                className={ShouldShowLabel(true, props.label) ? mediaContainerWithLabel : mediaContainerNoLabel}
                id={streamId}>
                {showRenderLoading && <Spinner style={loadingStyle} label={`Rendering stream...`} size={SpinnerSize.xSmall} />}
            </div>
            <div>
                <Image {...imageProps}
                />
            </div>
            {ShouldShowLabel(true, props.label) &&
                (<div className={activeStreamBeingRendered ? LabelContainer : LabelContainerNoRelative}>
                    <Label className={videoHint}>{utils.getUsernameFromLabel(label)}</Label>
                </div>)
            }
            
        </div>

    );
};